import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Play, Sparkles, ChevronLeft, Trash2, RefreshCw } from 'lucide-react';
import OverviewTab from '@/features/project/OverviewTab';
import PromptsTab from '@/features/project/PromptsTab';
import ScansTab from '@/features/project/ScansTab';
import AEOInsightsTab from '@/features/project/AEOInsightsTab';
import AnalyticsTab from '@/features/project/AnalyticsTab';
import CompetitorsTab from '@/features/project/CompetitorsTab';
import CitationsTab from '@/features/project/CitationsTab';
import AuditReportTab from '@/features/project/AuditReportTab';
import ProjectSettingsTab from '@/features/project/ProjectSettingsTab';

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const scanIdParam = searchParams.get('scan') || null;
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const navigate = useNavigate();

  const loadProject = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}`);
      setProject(r.data);
    } catch (e) {
      toast.error('Failed to load project');
      navigate('/app/projects');
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => { loadProject(); }, [loadProject]);

  const runScan = async () => {
    if (running) return;
    setRunning(true);
    try {
      const r = await api.post(`/projects/${projectId}/scans`, {});
      toast.success('Scan started — this can take 30–90s.');
      setSearchParams({ tab: 'scans', scan: r.data.id });
    } catch (err) {
      // 402 (plan gate) already shows a rich Upgrade toast via the interceptor.
      if (err?.response?.status !== 402) {
        toast.error(errMsg(err, 'Failed to start scan'));
      }
    } finally {
      setRunning(false);
    }
  };

  const setTab = (tab) => {
    const params = { tab };
    if (scanIdParam && tab === 'scans') params.scan = scanIdParam;
    setSearchParams(params);
  };

  const del = async () => {
    if (!window.confirm('Delete this project and all its data?')) return;
    try {
      await api.delete(`/projects/${projectId}`);
      toast.success('Project deleted');
      navigate('/app/projects');
    } catch (_e) {
      toast.error('Delete failed');
    }
  };

  const projectCtx = useMemo(() => ({ project, reload: loadProject }), [project, loadProject]);

  if (loading || !project) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 w-full sm:w-auto sm:flex-1">
          <button onClick={() => navigate('/app/projects')} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2" data-testid="project-back-link">
            <ChevronLeft className="h-3.5 w-3.5" /> All projects
          </button>
          <div className="flex items-center gap-3">
            <div className="flex shrink-0 h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--accent)/0.12)] text-[hsl(var(--accent))]">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className="font-display text-2xl font-semibold tracking-tight break-words" data-testid="project-title">{project.name}</h1>
              <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-2 break-words">
                {project.domain && <span>{project.domain}</span>}
                {project.industry && <><span>·</span><span>{project.industry}</span></>}
                {(project.competitors || []).slice(0, 3).map((c) => (
                  <Badge key={c} variant="secondary" className="text-[11px]">vs {c}</Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadProject} data-testid="project-refresh-button"><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>
          <Button size="sm" onClick={runScan} disabled={running} data-testid="project-run-scan-button">
            {running ? <><Sparkles className="h-4 w-4 mr-1.5 animate-pulse" /> Starting…</> : <><Play className="h-4 w-4 mr-1.5" /> Run scan</>}
          </Button>
          <Button variant="ghost" size="sm" onClick={del} title="Delete project" data-testid="project-delete-button">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap justify-start h-auto p-1">
          <TabsTrigger value="overview" data-testid="project-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="prompts" data-testid="project-tab-prompts">Prompts</TabsTrigger>
          <TabsTrigger value="scans" data-testid="project-tab-scans">Scans</TabsTrigger>
          <TabsTrigger value="aeo-insights" data-testid="project-tab-aeo-insights">AEO Insights</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="project-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="competitors" data-testid="project-tab-competitors">Competitors</TabsTrigger>
          <TabsTrigger value="citations" data-testid="project-tab-citations">Citations</TabsTrigger>
          <TabsTrigger value="audit" data-testid="project-tab-audit">Audit Report</TabsTrigger>
          <TabsTrigger value="settings" data-testid="project-tab-settings">Settings</TabsTrigger>
        </TabsList>
        <div className="mt-6">
          <TabsContent value="overview"><OverviewTab {...projectCtx} /></TabsContent>
          <TabsContent value="prompts"><PromptsTab {...projectCtx} /></TabsContent>
          <TabsContent value="scans"><ScansTab {...projectCtx} initialScanId={scanIdParam} onSelectScan={(id) => setSearchParams({ tab: 'scans', scan: id })} /></TabsContent>
          <TabsContent value="aeo-insights"><AEOInsightsTab {...projectCtx} /></TabsContent>
          <TabsContent value="analytics"><AnalyticsTab {...projectCtx} /></TabsContent>
          <TabsContent value="competitors"><CompetitorsTab {...projectCtx} /></TabsContent>
          <TabsContent value="citations"><CitationsTab {...projectCtx} /></TabsContent>
          <TabsContent value="audit"><AuditReportTab {...projectCtx} /></TabsContent>
          <TabsContent value="settings"><ProjectSettingsTab {...projectCtx} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
